<template>
  <div>
    <mf-card-container>
      <template v-slot:title-complement>
        <span class="title d-flex align-center">
          Configuração WhatsApp
          <v-icon class="channel-icon ml-2">mdi-whatsapp</v-icon>
        </span>
      </template>

      <div class="d-flex flex-column mt-5" style="gap: 8px">
        <span class="mf-subtitle">Selecionar números disparadores</span>
        <span class="mf-description">Selecione os números cadastrados que irão disparar mensagens nessa campanha.</span>
        <div :class="['mt-6', 'selected-numbers-container', containerVariant]">
          <mf-icon v-if="selectedWhatsappNumbers.length === 0" icon="warning" size="19" fill color="vermelho-0" />
          <span :class="['selected-numbers-text', textVariant]">{{ selectedNumbersText }}</span>
        </div>
        <select-whatsapp-number-dialog ref="selectWhatsappNumberDialog" :pre-selected-numbers="selectedWhatsappNumbers" @save="handleWhatsappSave" />
      </div>
    </mf-card-container>

    <mf-card-container class="mt-5" title="Mensagem">
      <template v-slot:headers>
        <div class="d-flex" style="gap: 16px">
          <best-offer-configuration-dialog channel="whatsapp" :offer-type="selectedOfferType" @updated-variables="countMessageCharacters" />
          <template-message-modal v-model="selectedTemplate" :mode="['WHATSAPP']" />
        </div>
      </template>

      <div class="message-input-container">
        <div class="d-flex flex-column flex-50">
          <file-upload-card class="file-upload" :file-properties.sync="fileProperties" />
          <span class="mf-subtitle">Texto da mensagem</span>

          <input-with-best-offer
            v-model="message"
            class="mt-3"
            channel="whatsapp"
            field="text"
            :offer-type="selectedOfferType"
            :char-count="charCountByOfferType[selectedOfferType]"
            :max-length="maxLength"
            :error="inputErrorMessage.length > 0"
            :error-message="inputErrorMessage"
          />
        </div>

        <div class="d-flex flex-column flex-50">
          <span class="mf-subtitle--cinza-0">Inspeção</span>

          <offer-type-options-menu :selected-offer-type.sync="selectedOfferType" :invalid-offer-types="invalidOfferTypes" :items="offerTypeOptions" />

          <div class="smartphone">
            <div class="smartphone-header">
              <div class="d-flex flex-row align-center" style="gap: 12px">
                <mf-icon icon="arrow_back_ios" color="cinza-l70" />
                <mf-icon color="cinza-l70" fill large>lens</mf-icon>
                <div class="smartphone-rectangle"></div>
              </div>
              <div class="d-flex flex-row align-center" style="gap: 12px">
                <div class="smartphone-square"></div>
                <div class="smartphone-square"></div>
              </div>
            </div>

            <div class="smartphone-content">
              <div class="smartphone-message">
                <div v-if="fileUrl" class="smartphone-image">
                  <v-img max-height="200px" width="100%" eager position="center center" :src="fileUrl" />
                  <div class="d-flex justify-end pa-2">
                    <download-file-button :url="fileUrl" :label="downloadFileButtonLabel" />
                  </div>
                </div>
                <whatsapp-preview :message="message" :offer-type="selectedOfferType" channel="whatsapp" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </mf-card-container>
  </div>
</template>

<script>
import { channelsForm } from '@/modules/maria/campanha-da-maria-v2/mixins/channels-form'
import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign.js'
import { charCount } from '@/modules/maria/campanha-da-maria-v2/mixins/char-count'
import { required } from 'vuelidate/lib/validators'
import { notOnlySpace } from '@/helpers/validators.js'
import { bestOfferValidations } from '@/modules/maria/campanha-da-maria-v2/mixins/best-offer-validations'

export default {
  name: 'WhatsappForm',
  components: {
    SelectWhatsappNumberDialog: () =>
      import('@/modules/maria/campanha-da-maria-v2/views/components/ChannelsConfiguration/ChannelSettingsForm/WhatsappForm/SelectWhatsappNumberDialog.vue'),
    BestOfferConfigurationDialog: () =>
      import('@/modules/maria/campanha-da-maria-v2/views/components/ChannelsConfiguration/BestOfferConfiguration/BestOfferConfigurationDialog.vue'),
    TemplateMessageModal: () => import('@/components/template-message/TemplateMessageModal.vue'),
    InputWithBestOffer: () => import('../InputWithBestOffer.vue'),
    FileUploadCard: () => import('@/modules/campanhas/whatsapp/views/components/FileUploadCard.vue'),
    DownloadFileButton: () => import('@/components/DownloadFileButton.vue'),
    WhatsappPreview: () =>
      import('@/modules/maria/campanha-da-maria-v2/views/components/ChannelsConfiguration/ChannelSettingsForm/WhatsappForm/WhatsappPreview.vue'),
  },
  mixins: [channelsForm, mariaCampaign, charCount, bestOfferValidations],
  data: () => ({
    selectedWhatsappNumbers: [],
    message: '',
    inputErrorMessage: [],
    selectedTemplate: {},
    fileProperties: {
      data_url: '',
      thumbnail_url: '',
      mimetype: '',
      filename: '',
    },
    maxLength: 2000,
    charCountByOfferType: {},
  }),
  computed: {
    channelSettings() {
      return this.campaignSettings.channels.whatsapp || {}
    },
    invalidOfferTypes() {
      const invalidOfferTypes = []

      this.offerTypeOptions.forEach((option) => {
        const charCount = this.charCountByOfferType[option.value]

        if (charCount > this.maxLength) {
          invalidOfferTypes.push(option.value)
        }
      })

      return invalidOfferTypes
    },
    downloadFileButtonLabel() {
      if (this.fileProperties && this.fileProperties.mimetype === 'application/pdf') {
        return 'Baixar documento'
      }
      return 'Baixar imagem'
    },
    fileUrl() {
      return this.fileProperties && this.fileProperties.thumbnail_url
    },
    containerVariant() {
      let selectedNumbersContainer = 'selected-numbers-container'
      return this.selectedWhatsappNumbers.length > 0 ? `${selectedNumbersContainer}--blue` : `${selectedNumbersContainer}--red`
    },
    textVariant() {
      let selectedNumbersText = 'selected-numbers-text'
      return this.selectedWhatsappNumbers.length > 0 ? `${selectedNumbersText}--blue` : `${selectedNumbersText}--red`
    },
    selectedNumbersText() {
      const totalSelected = this.selectedWhatsappNumbers.length
      if (totalSelected === 0) {
        return 'Nenhum número selecionado'
      } else if (totalSelected === 1) {
        return '1 Número selecionado'
      } else if (totalSelected === this.$refs?.selectWhatsappNumberDialog?.items?.length) {
        return `Todos os números selecionados (${totalSelected})`
      } else if (totalSelected > 1) {
        return `${totalSelected} Números selecionados`
      }
      return ''
    },
    getWhatsappType() {
      const { mimetype } = this.fileProperties
      if (!mimetype) return 'TEXT'
      else if (mimetype === 'application/pdf') return 'DOCUMENT'
      else return 'IMAGE'
    },
  },
  watch: {
    selectedTemplate(newValue) {
      if (newValue.message !== this.message) {
        this.message = newValue.message
      }
    },
    message(newValue) {
      this.clearInputErrorMessage()
      this.countMessageCharacters()
    },
  },
  mounted() {
    this.selectedWhatsappNumbers = this.channelSettings.senders || []
    this.fileProperties = this.channelSettings.message.attachment || {
      data_url: '',
      thumbnail_url: '',
      mimetype: '',
      filename: '',
    }
    this.message = this.channelSettings.message.text || ''
    this.createCharCount()
    this.countMessageCharacters()
  },
  methods: {
    clearInputErrorMessage() {
      this.inputErrorMessage = []
    },
    createCharCount() {
      const charCountByOfferType = {}
      this.offerTypeOptions.forEach((option) => {
        charCountByOfferType[option.value] = 0
      })
      this.$set(this, 'charCountByOfferType', charCountByOfferType)
    },
    countMessageCharacters() {
      this.offerTypeOptions.forEach((option) => {
        this.charCountByOfferType[option.value] = this.countTextWithVariables(this.message, 'whatsapp', 'text', option.value)
      })
    },
    handleWhatsappSave(newSelectedWhatsappNumbers) {
      this.selectedWhatsappNumbers = newSelectedWhatsappNumbers
    },
    validInput() {
      this.clearInputErrorMessage()

      let hasErrors = false

      this.inputErrorMessage.push(...this.validateVariables(this.message))

      this.offerTypeOptions.forEach((option) => {
        if (this.charCountByOfferType[option.value] > this.maxLength) {
          this.inputErrorMessage.push(`A mensagem ultrapassa o limite de ${this.maxLength} caracteres para ${option.text}.`)
          hasErrors = true
        }
      })

      if (this.hasMoreThanOneBestOffer(this.message)) {
        this.inputErrorMessage.push('A mensagem não pode conter mais de uma variável de melhor oferta.')
        hasErrors = true
      }

      if (this.hasInvalidBestOffer(this.message)) {
        this.inputErrorMessage.push('A mensagem contém uma variável de melhor oferta inválida.')
        hasErrors = true
      }

      if (this.selectedWhatsappNumbers.length === 0) {
        this.$snackbar({ message: 'Selecione ao menos um número de WhatsApp.', snackbarColor: 'error' })
        hasErrors = true
      }

      if (!this.$v.message.required) {
        this.inputErrorMessage.push('A mensagem não pode ser vazia.')
        hasErrors = true
      }

      if (!this.$v.message.notOnlySpace) {
        this.inputErrorMessage.push('Não é permitido salvar apenas espaços.')
        hasErrors = true
      }

      if (!this.hasEnoughProducts(this.message)) {
        this.inputErrorMessage.push(
          'Para utilizar essa quantidade de produtos na variável de melhor oferta, você precisa ter pelo menos essa quantidade de produtos no passo 2. Por favor volte e insira os produtos faltantes.'
        )
        hasErrors = true
      }

      if (!this.isPricesCorrect('whatsapp', 'text', 'offer_sales', this.message)) {
        this.inputErrorMessage.push(
          'Para utilizar as subvariáveis de preço você precisa configurar seus respectivos valores no passo 2. Por favor volte e preencha os campos deixados em branco ou remova os preços das suas mensagens.'
        )
        hasErrors = true
      }

      return !hasErrors
    },
    getChannelData() {
      return {
        ...this.channelSettings,
        senders: this.selectedWhatsappNumbers,
        targeting_mode: this.hasSegments ? 'SEGMENTED' : 'LOYALTY',
        message: {
          text: this.message,
          amount_best_offers: null,
          attachment: this.fileProperties.data_url ? this.fileProperties : null,
          type: this.getWhatsappType,
        },
      }
    },
  },
  validations() {
    return {
      message: {
        required,
        notOnlySpace,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.selected-numbers-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  height: 44px;
  width: fit-content;

  &--blue {
    background: #d2e9ff80;
  }

  &--red {
    background: #e0321026;
  }
}

.selected-numbers-text {
  font-weight: 600;
  font-size: 14px;

  &--blue {
    color: $marca-0-l20;
  }

  &--red {
    color: $vermelho-d20;
  }
}

.message-input-container {
  display: flex;
  flex-direction: row;
  gap: 60px;
  padding: 0 32px;
}

.flex-50 {
  flex: 50%;
  min-width: 40%;
}

::v-deep .file-upload.v-card.v-sheet.theme--light {
  border: none;
  box-shadow: none !important;
  padding: 0 !important;

  .v-card__text {
    padding: 0;
  }
}

.smartphone {
  margin-top: 24px;
  width: 100%;
  min-height: 560px;
  background-color: $cinza-l80;
  display: flex;
  flex-direction: column;

  .smartphone-header {
    height: 64px;
    padding: 12px 16px;
    background-color: $cinza-l90;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .smartphone-rectangle {
      width: 111px;
      height: 16px;
      border-radius: 6px;
      background-color: $cinza-l70;
    }

    .smartphone-square {
      width: 24px;
      height: 24px;
      border-radius: 6px;
      background-color: $cinza-l70;
    }
  }

  .smartphone-content {
    display: flex;
    align-items: flex-end;
    height: 100%;

    .smartphone-message {
      margin: 16px;
      margin-top: 58px;
      width: 80%;
      height: fit-content;
      padding: 8px 10px;
      border-radius: 16px;
      background-color: white;

      .smartphone-image {
        background-color: $cinza-l90;
        margin-bottom: 10px;
      }
    }
  }
}
</style>
